import { IConstraint, IRestaurantMealPeriod } from '../types/brandMenu';
import { ParsedModifierGroup } from '../types/menu';
import logger from './logger';
import { parseUpsellConditions } from './cart.upsellConditions';
import { isSuppressedUpsell } from './groupedUpsellDeclined';
import {
  checkMealPeriodAvailabilityAndActive,
  evaluateItemNotOnCheckConstraint,
  isConstraintApplicableToRestaurantGroups,
} from './menuUnification.utils';
import { toConstraintKey } from '../redux/features/experiments/experiments.utils';

/**
 * HITL-1761 — slot-bound upsells, the agent's side of PRVA-16210.
 *
 * A menu author marks an upsell constraint `binds_to:<slot>` (CJ's
 * `upsell_shake_in_combo` → the combo's `drink_choice`). The AI then asks that
 * slot in the upsell's wording ("what drink, or upgrade to an Oreo shake?") and
 * the answer lands in the slot itself: nothing is added to the cart. The upsell
 * hangs off a `post_item_complete_*` carrier no agent can reach, so HITL finds
 * it here, by the slot it is bound to, and makes the same offer when the agent
 * asks that slot.
 *
 * Everything here is pure so each gate can be unit-tested without a store.
 */

const BINDS_TO_PREFIX = 'binds_to:';
const BINDS_TO_PATTERN = /^binds_to:([a-z0-9_]+)$/;

const flattenConditions = (conditions?: string[] | null): string[] =>
  (conditions ?? [])
    .flatMap((condition) =>
      typeof condition === 'string' ? condition.split('\n') : []
    )
    .map((condition) => condition.trim())
    .filter(Boolean);

/** The slot a constraint is bound to, or null when it carries no valid `binds_to:`. */
export const parseBindsTo = (conditions?: string[] | null): string | null => {
  for (const condition of flattenConditions(conditions)) {
    const match = condition.toLowerCase().match(BINDS_TO_PATTERN);
    if (match) return match[1];
  }
  return null;
};

/**
 * `{ slot: the upsell constraints bound to it }`. Same validation as the AI's
 * `build_slot_bound_upsell_map`: an upsell whose `binds_to:` is malformed,
 * whose slot is not on the menu, or whose default item (what a "yes" saves) is
 * not one of the slot's options is left out — HITL never makes an offer the AI
 * would not.
 */
export const buildSlotBoundUpsellMap = (
  constraints: Record<string, IConstraint>
): Record<string, string[]> => {
  const bySlot: Record<string, string[]> = {};
  Object.values(constraints).forEach((constraint) => {
    const conditions = flattenConditions(constraint.conditions);
    if (!conditions.some((c) => c.toLowerCase().startsWith(BINDS_TO_PREFIX))) {
      return;
    }
    const slot = parseBindsTo(conditions);
    const host = slot ? constraints[slot] : undefined;
    const defaultItem = constraint.upsell_default_item;
    if (!host || !defaultItem || !host.child_items?.includes(defaultItem)) {
      logger.warn(
        `[SLOT_BOUND] '${constraint.name}' is never offered: binds_to '${slot}' is malformed or not on the menu, or its default '${defaultItem}' is not one of that constraint's options`
      );
      return;
    }
    bySlot[slot as string] = [
      ...(bySlot[slot as string] ?? []),
      constraint.name,
    ];
  });
  return bySlot;
};

export interface SlotBoundOfferContext {
  enabled: boolean;
  bySlot: Record<string, string[]>;
  constraints: Record<string, IConstraint>;
  // Lowercase name of the cart item that owns the slot — only that item, not
  // its parents: CJ's `drink_choice` sits directly under the combo, so an
  // `upsell_excluded_items` entry names the combo itself. A slot nested deeper
  // would need the parent chain walked here.
  ancestorNames: string[];
  // Every item name in the cart, nested ones included.
  cartItemNames: string[];
  withheldConstraints: Set<string>;
  offeredUpsells: Record<string, true>;
  isExpressSuppressionActive: boolean;
  timezone?: string;
  restaurantMealPeriodsByName?: Record<string, IRestaurantMealPeriod>;
  currentRestaurantGroups?: string[];
}

export type SlotBoundHostGroup = Pick<
  ParsedModifierGroup,
  'name' | 'menuItems'
>;

const isOptionUnavailable = (modGroup: SlotBoundHostGroup, name: string) =>
  Object.values(modGroup.menuItems ?? {}).some(
    (item) =>
      item.name?.toLowerCase() === name.toLowerCase() &&
      item.available === false
  );

/**
 * A pilot offer bound to hours ("after 2pm") fails closed: without the store's
 * timezone or meal periods the shared check passes every period, which would
 * make the offer all day.
 */
const isMealPeriodKnown = (
  constraint: IConstraint,
  ctx: Pick<SlotBoundOfferContext, 'timezone' | 'restaurantMealPeriodsByName'>
) =>
  !constraint.meal_period ||
  (!!ctx.timezone && !!ctx.restaurantMealPeriodsByName);

/**
 * The upsell constraint to speak when the agent asks `modGroup`, or null to
 * ask the slot's own question. Each gate mirrors one the AI applies before its
 * offer; the AI's per-order mid-order cap is deliberately not mirrored (HITL
 * keeps no such counter).
 */
export const resolveSlotBoundOffer = (
  modGroup: SlotBoundHostGroup,
  ctx: SlotBoundOfferContext
): IConstraint | null => {
  if (!ctx.enabled) return null;
  const candidates = ctx.bySlot[modGroup.name];
  if (!candidates?.length) return null;

  // One constraint per flavour, each scoped to its own stores and hours (Oreo
  // at most pilot stores, Strawberry at AMS/CJB): pick by group, never by name.
  const constraint = candidates
    .map((name) => ctx.constraints[name])
    .find(
      (candidate) =>
        !!candidate &&
        isMealPeriodKnown(candidate, ctx) &&
        checkMealPeriodAvailabilityAndActive({
          mealPeriod: candidate.meal_period || null,
          timezone: ctx.timezone,
          restaurantMealPeriodsByName: ctx.restaurantMealPeriodsByName,
          entityName: candidate.name,
        }) &&
        isConstraintApplicableToRestaurantGroups({
          constraint: candidate,
          restaurantMealPeriodsByName: ctx.restaurantMealPeriodsByName || {},
          currentRestaurantGroups: ctx.currentRestaurantGroups,
        })
    );
  if (!constraint?.prompt) return null;

  const key = toConstraintKey(constraint.name);
  const skip = (reason: string) => {
    logger.debug(
      `[SLOT_BOUND] '${constraint.name}' not offered for /${modGroup.name}: ${reason}`
    );
    return null;
  };

  if (ctx.offeredUpsells[key]) return skip('already offered to this car');
  if (ctx.withheldConstraints.has(key)) return skip('withheld for this car');

  const conditions = parseUpsellConditions(
    flattenConditions(constraint.conditions)
  );
  const offered = [
    constraint.upsell_default_item ?? '',
    ...conditions.suppress_if_unavailable_items,
  ].filter(Boolean);
  if (offered.some((name) => isOptionUnavailable(modGroup, name))) {
    return skip("an offered option is 86'd");
  }
  if (
    !evaluateItemNotOnCheckConstraint(
      constraint,
      ctx.constraints,
      ctx.cartItemNames
    )
  ) {
    return skip('constraint_item_not_on_check failed');
  }
  if (isSuppressedUpsell(conditions.suppress_upsell_conditions)) {
    return skip('suppressed after a decline');
  }
  if (
    conditions.upsell_excluded_items.some((name) =>
      ctx.ancestorNames.includes(name)
    )
  ) {
    return skip('excluded for this item');
  }
  if (
    ctx.isExpressSuppressionActive &&
    conditions.do_not_upsell_in_express_mode &&
    !conditions.always_offer
  ) {
    return skip('express mode');
  }
  return constraint;
};

/**
 * The bound upsell shaped for `upsell.attempt`, so the agent's pick in the
 * slot answers it by name: its upsell items accept, and every other option of
 * the slot declines. Declines come from the slot's own options as well as the
 * constraint's, so a drink the menu added to the slot but not yet to the
 * upsell still counts as a decline.
 */
export const toSlotBoundAttemptGroup = (
  constraint: IConstraint,
  modGroup: SlotBoundHostGroup
): ParsedModifierGroup => {
  const upsellItems = constraint.upsell_items ?? [];
  const slotOptions = Object.values(modGroup.menuItems ?? {}).map(
    (item) => item.name
  );
  const declineItems = Array.from(
    new Set([...slotOptions, ...(constraint.child_items ?? [])])
  ).filter((name) => !!name && !upsellItems.includes(name));
  return {
    ...constraint,
    upsell_items: upsellItems,
    upsell_decline_items: declineItems,
  } as unknown as ParsedModifierGroup;
};
